"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.derivePubkeyFromSpub = derivePubkeyFromSpub;
exports.derivePrivkeyFromSpriv = derivePrivkeyFromSpriv;
const ed = __importStar(require("@noble/ed25519"));
const sha2_1 = require("@noble/hashes/sha2");
const hmac_1 = require("@noble/hashes/hmac");
const base_1 = require("@scure/base");
const SPUB_DATA_LEN = 78;
const SPRIV_DATA_LEN = 78;
const CHAINCODE_LEN = 32;
const SPUB_VERSION_MAGIC = 0x0420BD3A;
const SPRIV_VERSION_MAGIC = 0x0420B900;
const HARDENED_FLAG = 0x80000000;
function bigintToUint8Array(value, littleEndian = true) {
    const bytes = [];
    while (value > 0) {
        bytes.push(Number(value & BigInt(0xFF)));
        value >>= BigInt(8);
    }
    return new Uint8Array(littleEndian ? bytes : bytes.reverse());
}
class ExtendedPubkey {
    constructor(depth, parentFingerprint, childNumber, chainCode, sharedPubkey) {
        this.depth = depth;
        this.parentFingerprint = parentFingerprint;
        this.childNumber = childNumber;
        this.chainCode = chainCode;
        this.sharedPubkey = sharedPubkey;
    }
    static fromSpub(sPub) {
        const spubBytes = (0, base_1.createBase58check)(sha2_1.sha256).decode(sPub);
        // spubBytes should be 78 bytes long
        if (spubBytes.length !== SPUB_DATA_LEN) {
            throw new Error(`Invalid sPub length (got ${spubBytes.length}, expected ${SPUB_DATA_LEN})`);
        }
        const spubVersion = spubBytes.slice(0, 4);
        const version = new DataView(spubVersion.buffer).getUint32(0, false);
        if (version !== SPUB_VERSION_MAGIC) {
            throw new Error(`Invalid public key value - sPub encoded pubkey should always start with "spub"`);
        }
        const spubDepth = spubBytes[4];
        const spubParentFingerprint = spubBytes.slice(5, 9);
        const spubChildNumberBytes = spubBytes.slice(9, 13);
        const spubChildNumber = new DataView(spubChildNumberBytes.buffer).getUint32(0, false);
        const spubChainCode = spubBytes.slice(13, 45);
        if (spubBytes[45] !== 0) {
            throw new Error(`Invalid public key value - pubkey should always start with 0`);
        }
        const spubPublicKey = spubBytes.slice(46, 78);
        const pubkeyPoint = ed.ExtendedPoint.fromHex(spubPublicKey);
        return new ExtendedPubkey(spubDepth, spubParentFingerprint, spubChildNumber, spubChainCode, pubkeyPoint);
    }
    hdDerive(index) {
        if (index >= HARDENED_FLAG) {
            throw new Error(`Invalid derivation index - non hardened derivation is required (index should be < ${HARDENED_FLAG})`);
        }
        if (this.depth === 255) {
            throw new Error(`Invalid derivation depth - maximum derivation depth reached`);
        }
        this.depth += 1;
        this.parentFingerprint = new Uint8Array(4); // Todo: implement parent fingerprint
        const indexBuffer = new ArrayBuffer(4);
        const indexView = new DataView(indexBuffer);
        indexView.setUint32(0, index, false);
        const prk = hmac_1.hmac.create(sha2_1.sha512, this.chainCode)
            .update(this.sharedPubkey.toRawBytes())
            .update(new Uint8Array(indexBuffer))
            .digest();
        const chaincodeWide = hmac_1.hmac.create(sha2_1.sha512, prk).update(new Uint8Array([1])).digest();
        const delta = hmac_1.hmac.create(sha2_1.sha512, prk).update(chaincodeWide).update(new Uint8Array([2])).digest();
        let result = BigInt('0x' + ed.etc.bytesToHex(delta.reverse()));
        result = ed.etc.mod(result, ed.CURVE.n);
        if (result === BigInt(0)) {
            throw new Error(`Invalid derivation result - derived scalar is zero`);
        }
        this.chainCode = chaincodeWide.slice(0, CHAINCODE_LEN);
        this.sharedPubkey = this.sharedPubkey.add(ed.ExtendedPoint.BASE.multiply(result));
        if (this.sharedPubkey.is0()) {
            throw new Error(`Invalid public key value - derived public key is zero`);
        }
        return result;
    }
    derivePubkey(derivationPath) {
        if ((derivationPath.length + this.depth) > 255) {
            throw new Error(`Invalid derivation path length - allowed maximum derivation depth is 255`);
        }
        let deltaSum = BigInt(0);
        for (const idx of derivationPath) {
            deltaSum += this.hdDerive(idx);
            deltaSum = ed.etc.mod(deltaSum, ed.CURVE.n);
        }
        return deltaSum;
    }
}
class ExtendedPrivateKey {
    constructor(depth, parentFingerprint, childNumber, privateKey, chainCode) {
        this.depth = depth;
        this.parentFingerprint = parentFingerprint;
        this.childNumber = childNumber;
        this.privateKey = privateKey;
        this.chainCode = chainCode;
    }
    static fromSpriv(sPriv) {
        const sprivBytes = (0, base_1.createBase58check)(sha2_1.sha256).decode(sPriv);
        // sprivBytes should be 78 bytes long
        if (sprivBytes.length !== SPRIV_DATA_LEN) {
            throw new Error(`Invalid sPriv length (got ${sprivBytes.length}, expected ${SPRIV_DATA_LEN})`);
        }
        const sprivVersion = sprivBytes.slice(0, 4);
        const version = new DataView(sprivVersion.buffer).getUint32(0, false);
        if (version !== SPRIV_VERSION_MAGIC) {
            throw new Error(`Invalid public key value - sPub encoded pubkey should always start with "spub"`);
        }
        const sprivDepth = sprivBytes[4];
        const sprivParentFingerprint = sprivBytes.slice(5, 9);
        const sprivChildNumberBytes = sprivBytes.slice(9, 13);
        const sprivChildNumber = new DataView(sprivChildNumberBytes.buffer).getUint32(0, false);
        const sprivChainCode = sprivBytes.slice(13, 45);
        const sprivPrivateScalarBytes = sprivBytes.slice(46, 78);
        if ((sprivPrivateScalarBytes[31] >> 7) != 0) {
            throw new Error(`Invalid private key value - private key should be a canonical scalar (last byte should not have the high bit set)`);
        }
        const privateScalarCandidate = BigInt('0x' + ed.etc.bytesToHex(sprivPrivateScalarBytes.reverse()));
        // Todo: check whether this scalar is a valid private key
        const sprivPrivateScalar = privateScalarCandidate;
        return new ExtendedPrivateKey(sprivDepth, sprivParentFingerprint, sprivChildNumber, sprivPrivateScalar, sprivChainCode);
    }
    getPubkey() {
        return new ExtendedPubkey(this.depth, this.parentFingerprint, this.childNumber, this.chainCode, ed.ExtendedPoint.BASE.multiply(this.privateKey));
    }
    derive(derivationPath) {
        const matchingPubkey = this.getPubkey();
        const delta = matchingPubkey.derivePubkey(derivationPath);
        this.privateKey = ed.etc.mod(this.privateKey + delta);
        this.chainCode = matchingPubkey.chainCode;
        this.childNumber = matchingPubkey.childNumber;
        this.depth = matchingPubkey.depth;
        this.parentFingerprint = matchingPubkey.parentFingerprint;
    }
}
function derivePubkeyFromSpub(sPub, derivationPath) {
    let current = ExtendedPubkey.fromSpub(sPub);
    current.derivePubkey(derivationPath);
    return current.sharedPubkey.toHex();
}
function derivePrivkeyFromSpriv(sPriv, derivationPath) {
    const privKey = ExtendedPrivateKey.fromSpriv(sPriv);
    privKey.derive(derivationPath);
    return ed.etc.bytesToHex(bigintToUint8Array(privKey.privateKey));
}
//# sourceMappingURL=index.js.map